import { Box, Button, Checkbox, Heading, HStack, IconButton, Text, VStack } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { MdOutlineLogout, MdWarningAmber } from "react-icons/md";
import { RiFileEditLine, RiForbid2Line, RiLockPasswordLine, RiQrScan2Line } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import MainModel from "../../../components/Models/main-model";
import {
  adminModalContentProps,
  adminModalHeaderProps,
} from "../../../components/Models/admin-modal-theme";
import CTable from "../../../components/Table/table";
import { brandPalette } from "../../../constants/brandPalette";
import {
  openCreateStudent,
  openStudentReportModel,
  openSuspendReasonModel,
  openUserQRCodeModel,
} from "../../../modules/admin-modules/models/Actions";
import { addStudentNoteRequest } from "../../../modules/admin-modules/reports/Actions";
import {
  clearSessionRequest,
  deleteStudentRequest,
  restrictStudentRequest,
  resendPasswordRequest,
} from "../../../modules/admin-modules/students/Actions";
import SuspendReasonModal from "./models/suspend-reason";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
};

const iconBtnProps = {
  variant: "ghost",
  border: "0",
  size: "sm",
  color: C.primary,
  _hover: { bg: "rgba(91, 13, 22, 0.08)", color: C.secondary },
};

function normalizeStudent(row) {
  if (!row || typeof row !== "object") return row;
  const nested = row.student && typeof row.student === "object" ? row.student : null;
  if (!nested) return row;
  return { ...nested, ...row, id: nested.id ?? row.id };
}

function formatPhoneForWhatsApp(phone) {
  if (!phone) return null;
  let cleaned = phone.toString().replace(/[\s\-()[\]]/g, "");
  if (cleaned.match(/^(012|011|010|015)/)) {
    cleaned = `2${cleaned}`;
  } else if (cleaned.startsWith("+")) {
    cleaned = cleaned.substring(1);
  } else if (cleaned.startsWith("00")) {
    cleaned = cleaned.substring(2);
  }
  return cleaned;
}

function formatNoteDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function getStudentNotes(data) {
  if (Array.isArray(data?.notes)) return data.notes;
  if (Array.isArray(data?.student_notes)) return data.student_notes;
  return [];
}

function getLatestNote(notes) {
  if (!notes.length) return null;
  return [...notes].sort((a, b) => {
    const ta = new Date(a?.created_at || 0).getTime();
    const tb = new Date(b?.created_at || 0).getTime();
    return tb - ta;
  })[0];
}

function noteText(note) {
  if (!note) return "";
  if (typeof note === "string") return note;
  return note.note || "";
}

function PhoneWithWhatsApp({ phone }) {
  if (!phone) return <Text color="var(--text-muted)">—</Text>;
  return (
    <HStack spacing={2} alignItems="center" flexWrap="wrap">
      <Text wordBreak="break-word">{phone}</Text>
      <IconButton
        as="a"
        href={`https://wa.me/${formatPhoneForWhatsApp(phone)}`}
        target="_blank"
        rel="noopener noreferrer"
        variant="ghost"
        size="sm"
        color="#25D366"
        aria-label="WhatsApp"
        icon={<FaWhatsapp />}
        onClick={(e) => e.stopPropagation()}
        _hover={{ bg: "rgba(37, 211, 102, 0.12)" }}
      />
    </HStack>
  );
}

function NotesHistoryModal({ student, onClose }) {
  const notes = getStudentNotes(student).slice().sort((a, b) => {
    const ta = new Date(a?.created_at || 0).getTime();
    const tb = new Date(b?.created_at || 0).getTime();
    return tb - ta;
  });

  return (
    <MainModel
      openVar={Boolean(student)}
      closeFunc={onClose}
      size="lg"
      hideDefaultFooter
      contentProps={adminModalContentProps}
      headerProps={adminModalHeaderProps}
      header={
        <Heading
          fontSize="22px"
          fontWeight="800"
          color="var(--text-primary)"
          fontFamily="'Amiri','Tajawal',sans-serif"
        >
          Notes history{student?.name ? ` — ${student.name}` : ""}
        </Heading>
      }
    >
      <VStack align="stretch" spacing={3} pb={2}>
        {notes.length ? (
          notes.map((note, index) => (
            <Box
              key={note.id || `${note.created_at}-${index}`}
              p={4}
              bg="var(--surface-muted)"
              border="1px solid"
              borderColor={C.border}
              borderRadius="12px"
            >
              <Text fontWeight="600" whiteSpace="pre-wrap">
                {noteText(note) || "—"}
              </Text>
              {note.created_at ? (
                <Text fontSize="xs" color="var(--text-muted)" mt={2} fontWeight="600">
                  {formatNoteDate(note.created_at)}
                </Text>
              ) : null}
              {note.user?.name || note.note_by ? (
                <Text fontSize="xs" color="var(--text-muted)" mt={1} fontWeight="600">
                  Added by: {note.user?.name || note.note_by}
                </Text>
              ) : null}
            </Box>
          ))
        ) : (
          <Text color="var(--text-muted)" fontWeight="600">
            No notes yet
          </Text>
        )}
        <Button
          onClick={onClose}
          variant="outline"
          borderRadius="12px"
          fontWeight="800"
          minH="44px"
          borderColor="var(--border-subtle)"
        >
          Close
        </Button>
      </VStack>
    </MainModel>
  );
}

const StudentRosterTable = ({
  setAdd,
  setSelectedData,
  list = [],
  isLoading = false,
  count,
  pageNumber,
  setPageNumber,
  size,
  setPerPage,
  paginateClient = false,
  compact = false,
  calledParentNote = "",
  selectable = false,
  selectedIds = [],
  onToggleStudent,
}) => {
  const dispatch = useDispatch();
  const isRestrict = useSelector((state) => state.students.isRestrict);
  const isSuspend = useSelector((state) => state.students.isSuspend);
  const studentsLoading = useSelector((state) => state.students.isLoading);
  const addingNoteStudentId = useSelector((state) => state.reports.addingNoteStudentId);
  const [selected, setSelected] = useState({ id: "" });
  const [suspendStudent, setSuspendStudent] = useState(null);
  const [historyStudent, setHistoryStudent] = useState(null);

  const students = (Array.isArray(list) ? list : []).map(normalizeStudent);
  const paged = paginateClient
    ? students.slice(pageNumber * size, pageNumber * size + size)
    : students;
  const totalCount = count ?? students.length;
  const totalPage = Math.max(1, Math.ceil((totalCount || 0) / (size || 50)));

  useEffect(() => {
    setSelectedData({});
    setSelected({ id: "" });
  }, [pageNumber, setSelectedData]);

  const viewData = (row) => {
    const plain = students.find((s) => s.id === row?.id) ?? row;
    setSelectedData(plain);
  };

  const Data = paged.map((data, index) => {
    const notes = getStudentNotes(data);
    const latest = getLatestNote(notes);
    return {
      ...data,
      index: `#${pageNumber * size + index + 1}`,
      id: data?.id,
      select: (
        <Checkbox
          isChecked={selectedIds.map(String).includes(String(data?.id))}
          onChange={() => onToggleStudent?.(data?.id)}
          colorScheme="red"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      student_name: (
        <Text
          cursor="pointer"
          fontWeight="700"
          color={C.secondary}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedData(data);
            dispatch(openStudentReportModel());
          }}
        >
          {data?.name}
        </Text>
      ),
      group_name: data?.group?.name,
      phone: <PhoneWithWhatsApp phone={data?.phone} />,
      parent_phone: <PhoneWithWhatsApp phone={data?.parent_phone} />,
      last_note: latest ? (
        <VStack align="start" spacing={1} maxW="280px">
          <Text fontWeight="600" noOfLines={2}>
            {noteText(latest)}
          </Text>
          <Text fontSize="xs" color="var(--text-muted)" fontWeight="600">
            {formatNoteDate(latest.created_at)}
          </Text>
          <Button
            size="xs"
            variant="link"
            color={C.primary}
            fontWeight="800"
            onClick={(e) => {
              e.stopPropagation();
              setHistoryStudent(data);
            }}
          >
            Show more
          </Button>
        </VStack>
      ) : (
        <Text color="var(--text-muted)">—</Text>
      ),
      called_parent: (
        <Button
          size="sm"
          borderRadius="10px"
          fontWeight="800"
          bg={C.primary}
          color="white"
          _hover={{ bg: C.secondary }}
          isLoading={String(addingNoteStudentId) === String(data?.id)}
          onClick={(e) => {
            e.stopPropagation();
            if (!calledParentNote || !data?.id) return;
            dispatch(
              addStudentNoteRequest({
                student_id: data.id,
                note: calledParentNote,
                refreshPerformance: false,
              })
            );
          }}
        >
          Called Parent
        </Button>
      ),
      code: (
        <IconButton
          {...iconBtnProps}
          aria-label="QR code"
          fontSize="20px"
          icon={<RiQrScan2Line />}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedData(data);
            dispatch(openUserQRCodeModel());
          }}
        />
      ),
      delete: (
        <IconButton
          {...iconBtnProps}
          aria-label={data?.deleted_at == null ? "Suspend student" : "Restore student"}
          fontSize="20px"
          icon={
            <MdWarningAmber color={data?.deleted_at == null ? "#9b2c2c" : "#1e5c3a"} />
          }
          onClick={(e) => {
            e.stopPropagation();
            setSelectedData(data);
            if (data?.deleted_at != null) {
              dispatch(deleteStudentRequest(data?.id));
            } else {
              setSuspendStudent(data);
              dispatch(openSuspendReasonModel());
            }
          }}
          isLoading={selected.id === data?.id ? isSuspend : false}
        />
      ),
      restrict: (
        <IconButton
          {...iconBtnProps}
          aria-label="Restrict access"
          fontSize="20px"
          isLoading={selected.id === data?.id ? isRestrict : false}
          icon={<RiForbid2Line color={data?.restrict === 0 ? "#9b2c2c" : "#1e5c3a"} />}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedData(data);
            const formData = new FormData();
            formData.append("_method", "PUT");
            if (data?.restrict === 0) {
              formData.append("restrict", 0);
            } else {
              formData.append("restrict", 1);
            }
            dispatch(
              restrictStudentRequest({
                id: data?.id,
                formData,
              })
            );
          }}
        />
      ),
      clearSession: (
        <IconButton
          {...iconBtnProps}
          aria-label="Clear session"
          fontSize="20px"
          icon={<MdOutlineLogout />}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedData(data);
            dispatch(clearSessionRequest(data?.id));
          }}
        />
      ),
      edit: (
        <IconButton
          {...iconBtnProps}
          aria-label="Edit student"
          fontSize="20px"
          icon={<RiFileEditLine />}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedData(data);
            setAdd(false);
            dispatch(openCreateStudent());
          }}
        />
      ),
      resendPassword: (
        <IconButton
          {...iconBtnProps}
          aria-label="Resend password"
          fontSize="20px"
          icon={<RiLockPasswordLine />}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedData(data);
            setSelected({ id: data?.id });
            dispatch(resendPasswordRequest(data?.id));
          }}
          isLoading={selected.id === data?.id ? studentsLoading : false}
        />
      ),
    };
  });

  const columns = compact
    ? [
        ...(selectable ? [{ Header: "", accessor: "select" }] : []),
        { Header: "#", accessor: "index" },
        { Header: "Student name", accessor: "student_name" },
        { Header: "Code", accessor: "user_name" },
        { Header: "Group", accessor: "group_name" },
        { Header: "Phone", accessor: "phone" },
        { Header: "Parent phone", accessor: "parent_phone" },
        { Header: "Last note", accessor: "last_note" },
        { Header: "Called parent", accessor: "called_parent" },
        { Header: "Suspend", accessor: "delete" },
      ]
    : [
        { Header: "#", accessor: "index" },
        { Header: "Student name", accessor: "student_name" },
        { Header: "Code", accessor: "user_name" },
        { Header: "Group", accessor: "group_name" },
        { Header: "QR", accessor: "code" },
        { Header: "Phone", accessor: "phone" },
        { Header: "Suspend", accessor: "delete" },
        { Header: "Clear session", accessor: "clearSession" },
        { Header: "Restrict", accessor: "restrict" },
        { Header: "Resend password", accessor: "resendPassword" },
        { Header: "Edit", accessor: "edit" },
      ];

  return (
    <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
      <Box
        bg="var(--surface-raised)"
        border="1px solid"
        borderColor={C.border}
        borderRadius="16px"
        overflow="hidden"
        boxShadow="sm"
      >
        <CTable
          selectedData={viewData}
          Columns={columns}
          Data={Data}
          path="openModel"
          Title=""
          subTitle=""
          btnTitle=""
          placeHolder=""
          noSearchBar
          noFilter
          footerBtnTitle
          filterList
          addButton
          addButtonTitle=""
          setPage={() => {}}
          setPerPage={setPerPage}
          currentpage={pageNumber}
          setPageNumber={setPageNumber}
          noSecondSearchBar
          isLoading={isLoading}
          perPage={size}
          totalPage={totalPage}
          wrapperProps={{ maxH: "unset", overflow: "auto" }}
          tableProps={{ bg: "transparent", borderRadius: "0", mb: 0 }}
          theadProps={{ bg: "var(--surface-muted)", color: "var(--text-primary)" }}
        />
      </Box>

      <SuspendReasonModal studentData={suspendStudent} />
      <NotesHistoryModal student={historyStudent} onClose={() => setHistoryStudent(null)} />
    </Box>
  );
};

export default StudentRosterTable;
